/**
 * Filtro global de período do painel. Um único controle define a granularidade
 * (dia/semana/mês) E o intervalo de datas (por preset rápido ou por calendário),
 * valendo para todos os gráficos de série temporal — em vez de um toggle por card.
 */

// Bimestres aproximados do calendário letivo brasileiro (fev–dez, recesso em
// julho); ajustar aqui se a rede definir datas oficiais por escola.
const BIMESTRES = {
  b1: ["02-01", "04-30"],
  b2: ["05-01", "07-15"],
  b3: ["07-16", "09-30"],
  b4: ["10-01", "12-31"]
};

export const PRESET_LABEL = {
  ano: "Ano letivo inteiro",
  "7d": "Últimos 7 dias",
  "30d": "Últimos 30 dias",
  "90d": "Últimos 90 dias",
  b1: "1º bimestre",
  b2: "2º bimestre",
  b3: "3º bimestre",
  b4: "4º bimestre",
  s1: "1º semestre",
  s2: "2º semestre",
  custom: "Período personalizado"
};

/**
 * Granularidade automática a partir do tamanho da janela escolhida no
 * calendário. Sem intervalo (ano inteiro) agrega por mês.
 */
export function granDeIntervalo(inicio, fim) {
  if (!inicio || !fim) return "mes";
  const dias =
    (new Date(fim).getTime() - new Date(inicio).getTime()) / 86_400_000;
  if (dias <= 45) return "dia"; // um dia, uma semana, até ~um mês
  if (dias <= 120) return "semana"; // bimestre / trimestre
  return "mes"; // semestre, ano
}

const iso = d =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

/** Datas (inicio/fim) de um preset. "ano"/"custom" não têm datas próprias. */
export function presetDatas(preset, ano) {
  const hoje = new Date();
  const y = ano ?? hoje.getFullYear();
  // "Últimos N dias" ancora em hoje só quando o ano selecionado é o corrente.
  // Pra um ano letivo passado, hoje cairia fora do próprio ano (ex.: filtro
  // ano=2023 + "últimos 7 dias" gerava inicio/fim em 2026 — combinado com o
  // "ano=" no backend via AND, zerava os resultados). Ancora em 31/dez do ano
  // selecionado, que é a última data possível dentro dele.
  const referencia = y === hoje.getFullYear() ? hoje : new Date(y, 11, 31);

  if (preset === "7d" || preset === "30d" || preset === "90d") {
    const dias = preset === "7d" ? 7 : preset === "30d" ? 30 : 90;
    const ini = new Date(referencia);
    ini.setDate(ini.getDate() - dias);
    return { inicio: iso(ini), fim: iso(referencia) };
  }
  if (preset === "s1") return { inicio: `${y}-01-01`, fim: `${y}-06-30` };
  if (preset === "s2") return { inicio: `${y}-07-01`, fim: `${y}-12-31` };
  if (preset in BIMESTRES) {
    const [i, f] = BIMESTRES[preset];
    return { inicio: `${y}-${i}`, fim: `${y}-${f}` };
  }
  return {}; // ano letivo inteiro: sem recorte (o backend já filtra por ano)
}

/** Estado inicial: ano letivo inteiro, por mês. */
export function periodoInicial() {
  return { gran: "mes", preset: "ano" };
}

/** Aplica um preset: recalcula datas e deriva a granularidade da janela. */
export function aplicarPreset(preset, ano) {
  const { inicio, fim } = presetDatas(preset, ano);
  return { preset, inicio, fim, gran: granDeIntervalo(inicio, fim) };
}

/** Ajusta uma das datas do calendário; vira intervalo personalizado. */
export function comData(estado, campo, valor) {
  const inicio = campo === "inicio" ? valor || undefined : estado.inicio;
  const fim = campo === "fim" ? valor || undefined : estado.fim;
  return { preset: "custom", inicio, fim, gran: granDeIntervalo(inicio, fim) };
}

/** Presets rápidos exibidos no painel de filtro. */
export function presetsRapidos(_ano) {
  return ["7d", "30d", "90d", "ano", "b1", "b2", "b3", "b4", "s1", "s2"];
}

/** true se o período ISO (yyyy-mm-dd) cai dentro do intervalo. */
export function dentroDoPeriodo(periodoIso, estado) {
  const p = String(periodoIso).slice(0, 10);
  if (estado.inicio && p < estado.inicio) return false;
  if (estado.fim && p > estado.fim) return false;
  return true;
}

const MESES_CURTOS = [
  "jan",
  "fev",
  "mar",
  "abr",
  "mai",
  "jun",
  "jul",
  "ago",
  "set",
  "out",
  "nov",
  "dez"
];

function fmtData(isoStr) {
  const [, m, d] = isoStr.split("-").map(Number);
  return `${String(d).padStart(2, "0")} ${MESES_CURTOS[(m || 1) - 1]}`;
}

/** Texto curto pro botão do topbar. */
export function periodoResumo(e) {
  if (e.preset === "custom") {
    if (e.inicio && e.fim) {
      return e.inicio === e.fim
        ? fmtData(e.inicio)
        : `${fmtData(e.inicio)} – ${fmtData(e.fim)}`;
    }
    if (e.inicio) return `A partir de ${fmtData(e.inicio)}`;
    if (e.fim) return `Até ${fmtData(e.fim)}`;
    return "Período personalizado";
  }
  return PRESET_LABEL[e.preset];
}
